import { Collapse, ConfigProvider, Modal, Typography } from "antd";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import {
  ConflictRemovalFooter,
  removalLockProps,
  serverSupportsRemoval,
  useRemovalAction,
} from "components/modals/ConflictRemovalFooter";
import { useCallback, useMemo, useState } from "react";
import { colors } from "shared/theme/color";
import { translate, translateWithVars } from "shared/languages";
import { AffectedOccurrence } from "shared/types";
import { ClassCategory } from "shared/types/enum";
import { groupByDate } from "shared/utils";
import { localDateOnly, localTzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// Same threshold/reasoning as LeaveConflictWarningModal's MAX_NAMES_INLINE.
const MAX_NAMES_INLINE = 3;

// Same caps as the server's MAX_RULE_REMOVAL_SERIES /
// MAX_RULE_REMOVAL_OCCURRENCES (staff.ts). Above either the delete button is
// not offered, so the endpoint's 400 is never reached from the UI.
const MAX_REMOVABLE_SERIES = 100;
const MAX_REMOVABLE_OCCURRENCES = 300;

type RulePreview = { affected_occurrences: AffectedOccurrence[] };

const occurrenceSubtitle = (occurrence: AffectedOccurrence) => {
  if (occurrence.booked_customer_count === 0) {
    return translate("leave.conflict.noClientsYet");
  }
  if (
    occurrence.class_type === ClassCategory.Open ||
    occurrence.booked_customer_count > MAX_NAMES_INLINE
  ) {
    return translateWithVars("leave.conflict.participantsCount", {
      count: occurrence.booked_customer_count,
    });
  }
  return occurrence.booked_customer_names.join(", ");
};

// What "Hapus Kelas & Simpan" does to this row, as muted text (no badge):
// a started class is skipped; a recurring class is either cut from this
// session to the end of its series, or has only this session removed.
const removalSuffix = (occurrence: AffectedOccurrence) => {
  if (occurrence.has_started) return translate("conflict.startedSuffix");
  if (!occurrence.is_recurring || !occurrence.deletion_mode) return null;
  return translate(
    occurrence.deletion_mode === "series" ? "conflict.seriesSuffix" : "conflict.occurrenceSuffix",
  );
};

const OccurrenceCard = ({ occurrence }: { occurrence: AffectedOccurrence }) => {
  const start = localTzDate(occurrence.occurrence_timestamp);
  const end = start.add(occurrence.duration, "minute");
  const suffix = removalSuffix(occurrence);
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${colors.lineStrong}`,
        borderRadius: 12,
        background: colors.card,
      }}
    >
      <Text style={{ fontWeight: 500 }}>{occurrence.class_name}</Text>
      <div>
        <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
          {start.format("HH:mm")} - {end.format("HH:mm")}
        </Text>
        <Text
          type={occurrence.booked_customer_count > 0 ? undefined : "secondary"}
          style={{ fontSize: textPx(12.5), fontWeight: occurrence.booked_customer_count > 0 ? 500 : 400 }}
        >
          {" "}
          · {occurrenceSubtitle(occurrence)}
        </Text>
        {suffix && (
          <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
            {" "}
            · {suffix}
          </Text>
        )}
      </div>
    </div>
  );
};

const DayGroupHeader = ({ date, count }: { date: string; count: number }) => (
  <Text style={{ fontWeight: 600 }}>
    {localDateOnly(date).format("dddd, DD MMMM YYYY")}
    <Text style={{ color: colors.ink3, fontWeight: 500, marginLeft: 6 }}>
      · {count} {translate("leave.conflict.classCount")}
    </Text>
  </Text>
);

const DayGroupBody = ({ occurrences }: { occurrences: AffectedOccurrence[] }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {occurrences.map((occurrence) => (
      <OccurrenceCard
        key={`${occurrence.schedule_id}-${occurrence.occurrence_timestamp}`}
        occurrence={occurrence}
      />
    ))}
  </div>
);

const RuleConflictWarningContent = ({
  preview,
  description,
}: {
  preview: RulePreview;
  description: string;
}) => {
  const dayGroups = useMemo(() => groupByDate(preview.affected_occurrences), [preview]);
  return (
    <div>
      <Text style={modalSubtitleStyle}>{description}</Text>
      <Collapse
        ghost
        style={{ marginTop: 16 }}
        items={dayGroups.map(([date, occurrences]) => ({
          key: date,
          label: <DayGroupHeader date={date} count={occurrences.length} />,
          children: <DayGroupBody occurrences={occurrences} />,
        }))}
      />
    </div>
  );
};

// Whether the delete button can be offered: the server must support removal,
// something must be deletable, and the request must stay within the caps
// (counted the way the server plans it: one cut per "series" schedule, every
// row of an "occurrence" schedule).
const canOfferRemoval = (preview: RulePreview | null) => {
  const rows = preview?.affected_occurrences ?? [];
  if (!serverSupportsRemoval(rows)) return false;
  const live = rows.filter((row) => !row.has_started);
  const series = new Set(live.filter((r) => r.deletion_mode === "series").map((r) => r.schedule_id));
  const singles = live.filter((r) => r.deletion_mode !== "series").length;
  if (series.size + singles === 0) return false;
  return series.size <= MAX_REMOVABLE_SERIES && singles <= MAX_REMOVABLE_OCCURRENCES;
};

const RuleConflictWarningModal = ({
  open,
  preview,
  copyKey,
  onConfirm,
  onConfirmWithRemoval,
  onCancel,
}: {
  open: boolean;
  preview: RulePreview | null;
  copyKey: "working_hours_conflict" | "class_assignment_conflict";
  onConfirm: () => void;
  onConfirmWithRemoval?: () => Promise<void>;
  onCancel: () => void;
}) => {
  const { removing, runRemoval } = useRemovalAction(onConfirmWithRemoval);
  const t = (leaf: string) => translate(`class_settings.employee_management.${copyKey}.${leaf}`);
  return (
    <ConfigProvider theme={modalButtonsTheme}>
      <Modal
        open={open}
        title={<ModalHeader tone="amber" title={t("title")} />}
        {...removalLockProps(removing, onCancel)}
        destroyOnClose
        styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
        footer={
          <ConflictRemovalFooter
            removing={removing}
            canRemove={!!onConfirmWithRemoval && canOfferRemoval(preview)}
            removeLabel={t("remove")}
            confirmLabel={t("confirm")}
            onRemove={runRemoval}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        }
      >
        {preview && <RuleConflictWarningContent preview={preview} description={t("description")} />}
      </Modal>
    </ConfigProvider>
  );
};

/**
 * Gates a rule save (working hours or class assignment) behind a preview of
 * which of the instructor's near-future classes would violate the PROPOSED
 * rule. Same shape as useLeavePreview:
 *  - checkAndConfirm(form, onConfirm, onConfirmWithRemoval?): runs the
 *    preview; with affected occurrences it opens the warning modal and runs
 *    onConfirm (proceed anyway) or onConfirmWithRemoval (delete the affected
 *    classes and save, in one server call) on the matching button; with none
 *    it runs onConfirm immediately. A failed check fails open.
 *  - warning: the modal element to render in the caller's JSX.
 */
export const useRuleConflictPreview = <TForm,>(
  fetchPreview: (form: TForm) => Promise<RulePreview>,
  copyKey: "working_hours_conflict" | "class_assignment_conflict",
) => {
  const [pending, setPending] = useState<{
    preview: RulePreview;
    onConfirm: () => void;
    onConfirmWithRemoval?: () => Promise<void>;
  } | null>(null);

  const checkAndConfirm = useCallback(
    async (form: TForm, onConfirm: () => void, onConfirmWithRemoval?: () => Promise<void>) => {
      let preview: RulePreview;
      try {
        preview = await fetchPreview(form);
      } catch (error) {
        console.error(`${copyKey} preview failed; proceeding without warning.`, error);
        onConfirm();
        return;
      }
      if (preview.affected_occurrences.length === 0) {
        onConfirm();
        return;
      }
      setPending({ preview, onConfirm, onConfirmWithRemoval });
    },
    [fetchPreview, copyKey],
  );

  const warning = (
    <RuleConflictWarningModal
      open={!!pending}
      preview={pending?.preview ?? null}
      copyKey={copyKey}
      onConfirm={() => {
        pending?.onConfirm();
        setPending(null);
      }}
      onConfirmWithRemoval={
        pending?.onConfirmWithRemoval
          ? async () => {
              await pending.onConfirmWithRemoval?.();
              setPending(null);
            }
          : undefined
      }
      onCancel={() => setPending(null)}
    />
  );

  return { checkAndConfirm, warning };
};
